/**
 * Лист «Как собрать на декабрь» и вход в него (04.11.2026).
 *
 * Собран из того, что уже есть: сводка отрезков — ячейками, как «Всего /
 * Отложено / Не хватает» в «Платежах»; шаги — строками «Как убрать
 * нехватку»; итог — зелёной строкой. Free видит тот же вход замком, как
 * «Разбор периода» в аналитике: заголовок, размытый первый шаг и «что даст».
 */
import { type CSSProperties } from 'react';
import { formatMoney, type Money } from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Icon } from './icon.js';
import type { GapPlan, GapStep, GapStepKind } from './gap-plan.js';

const rub = (m: Money): string => formatMoney((Math.round(m / 100) * 100) as Money);
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const LOOK: Record<GapStepKind, { icon: string; tone: string }> = {
  carry: { icon: 'calendar-check', tone: 'var(--ok)' },
  loan: { icon: 'pause', tone: 'var(--ink-2)' },
  goal: { icon: 'bullseye-arrow', tone: 'var(--ink-2)' },
  spend: { icon: 'bag-shopping-heart', tone: 'var(--ink-2)' },
  savings: { icon: 'piggy-bank', tone: 'var(--ok)' },
};

/** Что даст разбор — одной фразой: на входе у Pro и под замком у Free. */
export const planPromise = (p: GapPlan): string =>
  `${p.steps.length} ${p.steps.length === 1 ? 'шаг' : p.steps.length < 5 ? 'шага' : 'шагов'} с суммами`;

export function GapPlanEntry({ plan, pro, onOpen, onPro }: {
  plan: GapPlan; pro: boolean; onOpen: () => void; onPro: () => void;
}) {
  if (pro) {
    return (
      <button type="button" className="li w remrow gap-entry" onClick={onOpen}>
        <span className="picon" style={{ '--c': 'var(--ok)' } as CSSProperties}><Icon name="sparkles" size={16} /></span>
        <div className="nm"><b>Как собрать на {plan.monthAcc}</b><span>разбор SKOKA · {planPromise(plan)}</span></div>
        <Icon name="chevron-right" size={13} className="ch" />
      </button>
    );
  }
  const first = plan.steps[0];
  return (
    <div className="tease-card gap-entry">
      <div className="tease-h"><Icon name="sparkles" size={15} /><b>Разбор: как собрать на {plan.monthAcc}</b></div>
      {first && <p className="tease-blur" aria-hidden="true">{first.title} — {rub(first.gives)}. {first.sub}.</p>}
      <p className="gaf-advice">Покажет по твоим цифрам, что отложить, что поставить на паузу и что перенести, — и сколько это даст.</p>
      <button type="button" className="btn pri" onClick={onPro}>Открыть разбор — Pro</button>
    </div>
  );
}

export function GapPlanSheet({ plan, onStep, onAsk, onClose }: {
  plan: GapPlan;
  onStep: (s: GapStep) => void;
  /** «Спросить SKOKA» — если что-то не подходит. */
  onAsk?: () => void;
  onClose: () => void;
}) {
  const enough = plan.covered >= plan.need;
  const saving = plan.steps.some((s) => s.kind === 'spend');
  const row = (s: GapStep, k: number) => {
    const look = LOOK[s.kind];
    const body = (
      <>
        <span className="picon" style={{ '--c': look.tone } as CSSProperties}><Icon name={look.icon} size={16} /></span>
        <div className="nm"><b>{s.title}</b><span>{s.sub}</span></div>
        <b className="num remamt">+ {rub(s.gives)}</b>
      </>
    );
    // «Тратить меньше» — решение на каждый день, вести его некуда: строка без стрелки.
    if (s.kind === 'spend') return <div className="li w remrow" key={`${s.kind}:${k}`}>{body}</div>;
    return (
      <button type="button" className="li w remrow" key={`${s.kind}:${s.refId ?? k}`} onClick={() => onStep(s)}>
        {body}
        <Icon name="chevron-right" size={13} className="ch" />
      </button>
    );
  };
  return (
    <SheetWrap title={`Как собрать на ${plan.monthAcc}`} onClose={onClose}
      tip="Разбор SKOKA: суммы посчитаны по твоим данным, порядок — от того, что ничего не стоит, к тому, что откладывает желание. Подушку не трогаем.">
      <div className="gps">
        <div className="gps-sum">
          {plan.windows.map((w) => (
            <div key={w.month} className={w.left < 0 ? 'bad' : 'ok'}>
              <span>{cap(w.month)}</span>
              <b className="num">{w.left < 0 ? `− ${rub((-w.left) as Money)}` : `+ ${rub(w.left)}`}</b>
            </div>
          ))}
        </div>
        <p className="gaf-advice">
          В {plan.month} не хватит ≈ {rub(plan.need)}. {enough
            ? `${plan.steps.length === 1 ? 'Один шаг' : `${plan.steps.length} ${plan.steps.length < 5 ? 'шага' : 'шагов'}`} — и хватит${saving ? '' : ', урезать траты не придётся'}:`
            : 'Вот что поможет:'}
        </p>
        <section className="blk remgrp">
          {plan.steps.map(row)}
        </section>
        {enough && <div className="okline gap-ok">Собрано {rub(plan.covered)} из {rub(plan.need)} — в {plan.month} хватит</div>}
        {plan.tip && <div className="note gap-tip"><Icon name="lightbulb" size={14} /> {plan.tip}</div>}
        {plan.backup && (
          <section className="blk remgrp">
            <div className="blkhead"><b>Запасной вариант</b></div>
            {row(plan.backup, 99)}
          </section>
        )}
        <div className="note">Каждая строка ведёт туда, где это делается. Ничего не изменится, пока не подтвердишь.</div>
        {onAsk && <button type="button" className="btn" onClick={onAsk}>Спросить SKOKA</button>}
      </div>
    </SheetWrap>
  );
}
